import { NgZone, OnDestroy, inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { ExtendedGetResult } from '@fingerprintjs/fingerprintjs-pro';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, first, map, mergeMap, takeUntil } from 'rxjs/operators';
import { ApiClient } from '../../api/api-client';
import { ApiTypes } from '../../api/api-types';
import { Api } from '../../api/generated/api';
import { CONSTANTS } from '../../common/constants/constants';
import { CONSTANTS_ERRORS } from '../../common/constants/errors/errors.constants';
import { CONSTANTS_ROUTING } from '../../common/constants/routing/routing.constants';
import { Languages } from '../../common/enums/languages.enum';
import {
  IQuestionFreeTextRawValue,
  IQuestionOptionsRawValue,
  IQuestionPayload,
  QuestionType,
} from '../../core/interfaces/general.interface';
import { FingerprintService } from '../../core/providers/fingerprint/fingerprint.service';
import { DEMOGRAPHICS_CONSTANTS } from '../demographics/demographics.constants';
import { DemographicsService } from '../demographics/demographics.service';
import { IDemographicsDto } from '../demographics/interfaces/demographics-dto.interface';
import { FLEXIBLE_PORTAL_CONSTANTS } from '../flexible-portal/flexible-portal.constants';
import { FlexiblePortalService } from '../flexible-portal/flexible-portal.service';
import { WHEEL_OF_FORTUNE_ROUTING_CONSTANTS } from '../wheel-of-fortune/wheel-of-fortune-routing.constants';
import { WheelOfFortuneService } from '../wheel-of-fortune/wheel-of-fortune.service';
import { toCampaign } from './campaign.mapper';
import {
  ICampaign,
  ICampaignDraft,
  ICampaignDraftAnswer,
  ICampaignFormValue,
  ICampaignOptions,
  ICampaignPayload,
  ICampaignQuestion,
} from './interfaces/campaign.interface';

@Service()
export class CampaignService implements OnDestroy {
  private router = inject(Router);
  private fingerprintService = inject(FingerprintService);
  private zone = inject(NgZone);
  private demographicsService = inject(DemographicsService);
  private wheelOfFortuneService = inject(WheelOfFortuneService);
  private api = inject(ApiClient);
  private flexiblePortalService = inject(FlexiblePortalService);

  /** In-progress campaign answers expire after this idle window. */
  private readonly draftTtlMs: number = 30 * 60 * 1000;

  currentIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  questionLimitIndex = 0;
  currentIndex = 0;
  previousQuestionIndexes: number[] = [];
  private token: string;
  private campaign: ICampaign;
  private onDestroy$: Subject<void> = new Subject<void>();
  private responseUuid: string;

  backToPreviousQuestion(): void {
    this.currentIndex = this.previousQuestionIndexes.pop();

    if (this.currentIndex === undefined) {
      this.goToPortal();
    } else {
      this.currentIndex$.next(this.currentIndex);
    }
  }

  /** Emits `null` once a 404 or 403 has already sent the consumer to its page. */
  fetchCampaign$(
    venueUuid: string,
    visitorId: string
  ): Observable<ICampaign | ApiTypes.CampaignSurveyTokenResponseDto | null> {
    return this.api
      .get(Api.surveys.takeaway.campaign.venues, {
        pathParams: { venueUuid },
        fingerprint: visitorId,
      })
      .pipe(
        map(response => ('token' in response ? response : toCampaign(response))),
        catchError(response => {
          switch (response.status) {
            case CONSTANTS_ERRORS.HTTP_404: {
              this.zone.run(() => void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]));

              return of(null);
            }
            case CONSTANTS_ERRORS.HTTP_403: {
              this.zone.run(
                () =>
                  void this.router.navigate([
                    `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.BACK}`,
                  ])
              );

              return of(null);
            }
            default:
              break;
          }

          return throwError(response);
        })
      );
  }

  /** Emits `null` once a 404 has already sent the consumer to the error page. */
  fetchExactCampaign$(
    venueId: string,
    campaignId: string,
    visitorId: string
  ): Observable<ICampaign | null> {
    return this.api
      .get(Api.surveys.takeaway.campaign.venues.campaigns, {
        pathParams: { venueUuid: venueId, campaignUuid: campaignId },
        fingerprint: visitorId,
      })
      .pipe(
        map(toCampaign),
        catchError(response => {
          switch (response.status) {
            case CONSTANTS_ERRORS.HTTP_404: {
              this.zone.run(() => void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]));

              return of(null);
            }
            default:
              break;
          }

          return throwError(response);
        })
      );
  }

  saveCampaign(
    payload: ICampaignPayload
  ): Observable<ApiTypes.CampaignResponseAcceptedResponseDto> {
    return this.fingerprintService.visitorId$().pipe(
      first(),
      mergeMap(visitorId => {
        payload.visitorId = visitorId;

        return this.api
          .post(Api.surveys.takeaway.campaign.response, {
            body: payload as Api.surveys.takeaway.campaign.response.PostBody,
          })
          .pipe(
            catchError(error => {
              switch (error.status) {
                case CONSTANTS_ERRORS.HTTP_404: {
                  void this.router.navigate([CONSTANTS_ROUTING.ROUTES.NOT_FOUND]);

                  return of(null);
                }
                case CONSTANTS_ERRORS.HTTP_500: {
                  void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]);

                  return of(null);
                }
                default:
                  break;
              }

              return throwError(error);
            })
          );
      })
    );
  }

  goToNextStep(
    question: ICampaignQuestion,
    formValue: IQuestionOptionsRawValue | IQuestionFreeTextRawValue
  ): void {
    this.loadQuestion(this.getNextStep(question, formValue));
  }

  getNextStep(
    question: ICampaignQuestion,
    formValue: IQuestionOptionsRawValue | IQuestionFreeTextRawValue
  ): number {
    if (!question.isCustomNextStep) {
      const questionIndex: number = this.campaign.campaign.questions.findIndex(
        (campaignQuestion: ICampaignQuestion) => campaignQuestion.uuid === question.uuid
      );

      return questionIndex + 1 < this.campaign.campaign.questions.length ? questionIndex + 2 : null;
    }

    return this.getCustomNextStep(question, formValue);
  }

  setPortalToken(token: string): void {
    this.token = token;
    localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.PORTAL_TOKEN, this.token);
  }

  setCampaign(campaign: ICampaign): void {
    this.campaign = campaign;
  }

  getCampaignPayload(
    venueUuid: string,
    answers: IQuestionPayload[],
    lang: Languages
  ): ICampaignPayload {
    return {
      venueUuid,
      answers,
      lang,
      campaignUuid: this.campaign.campaign.uuid,
    };
  }

  redirectAfterSave(responseUuid: string): void {
    this.responseUuid = responseUuid;

    if (this.campaign.info.wheel) {
      this.wheelOfFortuneService.setWheelConfig(this.campaign.info.wheel);

      if (this.campaign.info.wheel.isActive) {
        this.wheelOfFortuneService.campaignResponseUuid = responseUuid;
        this.wheelOfFortuneService.campaignUuid = this.campaign.campaign.uuid;
      }
    }
    if (
      this.campaign.demographics.isEnabled &&
      (this.campaign.demographics.shouldAskForAgeGroup ||
        this.campaign.demographics.shouldAskForGender)
    ) {
      this.demographicsService.setDemographicsConfig(this.campaign.demographics);
      this.demographicsService.setGenderOptionsSource({
        campaignUuid: this.campaign.campaign.uuid,
      });
      this.demographicsService.isCampaignMode = true;
      this.goToDemographicsForm();
    } else {
      this.goToHappyWheelOrAdvertisement();
    }
  }

  goToFinalPage(): void {
    void this.router.navigate([
      `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.THANK_YOU}`,
    ]);
  }

  goToHappyWheelAfterDemographicsCanceling(demographicsDto: IDemographicsDto): void {
    this.saveSurveyDemographics$(demographicsDto).pipe(takeUntil(this.onDestroy$)).subscribe();

    if (
      this.wheelOfFortuneService.getWheelConfig() &&
      this.wheelOfFortuneService.getWheelConfig().isDemographicsEnabled
    ) {
      void this.router.navigate([
        `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.THANK_YOU}`,
      ]);
    } else {
      this.goToHappyWheelOrAdvertisement();
    }
  }

  saveDemographics(demographicsDto: IDemographicsDto): void {
    this.saveSurveyDemographics$(demographicsDto)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.goToHappyWheelOrAdvertisement());
  }

  /**
   * Persist the respondent's in-progress answers so the campaign can be resumed
   * after a reload. Stored in `sessionStorage` (scoped to the tab/session, so it
   * cannot leak to the next guest on a shared device) and keyed by question/
   * option uuid (so it stays small and survives server-side campaign edits).
   */
  saveCampaignAnswers(formValue: ICampaignFormValue, campaignId: string): void {
    const answers: ICampaignDraftAnswer[] = (formValue.questions ?? [])
      .map((question: ICampaignQuestion) => this.extractDraftAnswer(question))
      .filter((answer: ICampaignDraftAnswer | null): answer is ICampaignDraftAnswer => !!answer);

    if (!answers.length) {
      sessionStorage.removeItem(campaignId);

      return;
    }

    const draft: ICampaignDraft = { savedAt: Date.now(), answers };

    try {
      sessionStorage.setItem(campaignId, JSON.stringify(draft));
    } catch {
      // Drafting is best-effort — ignore quota / private-mode failures.
    }
  }

  getCampaignAnswers(campaignId: string): ICampaignDraftAnswer[] | null {
    let draft: ICampaignDraft | null = null;

    try {
      const raw: string | null = sessionStorage.getItem(campaignId);

      draft = raw ? (JSON.parse(raw) as ICampaignDraft) : null;
    } catch {
      draft = null;
    }

    if (!draft || !Array.isArray(draft.answers)) {
      return null;
    }

    if (Date.now() - draft.savedAt > this.draftTtlMs) {
      sessionStorage.removeItem(campaignId);

      return null;
    }

    return draft.answers;
  }

  removeStorage(campaignId: string): void {
    sessionStorage.removeItem(campaignId);
  }

  private extractDraftAnswer(question: ICampaignQuestion): ICampaignDraftAnswer | null {
    const options: ICampaignOptions[] = question.options ?? [];
    const selectedOptionUuids: string[] = options
      .filter((option: ICampaignOptions) => option.value && option.uuid)
      .map((option: ICampaignOptions) => option.uuid);
    const optionTexts: { optionUuid: string; text: string }[] = options
      .filter((option: ICampaignOptions) => option.uuid && option.text)
      .map((option: ICampaignOptions) => ({
        optionUuid: option.uuid,
        text: option.text,
      }));
    const value: string | undefined =
      typeof question.value === 'string' && question.value !== '' ? question.value : undefined;

    if (!selectedOptionUuids.length && !optionTexts.length && value === undefined) {
      return null;
    }

    return {
      questionUuid: question.uuid,
      ...(selectedOptionUuids.length ? { selectedOptionUuids } : {}),
      ...(optionTexts.length ? { optionTexts } : {}),
      ...(value !== undefined ? { value } : {}),
    };
  }

  sendFirstFingerprintDataAnalytics(
    campaignResponseUuid: string,
    data: ExtendedGetResult,
    isBlocked: boolean,
    checkInUuid?: string
  ) {
    const fingerprintDTO: ApiTypes.StoreFingerprintCommand = {
      campaignUuid: campaignResponseUuid,
      data: { ...data },
      isBlocked,
      checkInUuid,
    };

    return this.api.post(Api.surveys.takeaway.fingerprint, { body: fingerprintDTO });
  }

  sendFingerprintDataAnalytics(
    campaignResponseUuid: string,
    data: ExtendedGetResult,
    isBlocked: boolean,
    campaignUuid: string,
    checkInUuid?: string
  ) {
    const fingerprintDTO: ApiTypes.StoreFingerprintCommand = {
      campaignResponseUuid,
      data: { ...data },
      isBlocked,
      checkInUuid,
      campaignUuid,
    };

    return this.api.post(Api.surveys.takeaway.fingerprint, { body: fingerprintDTO });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private saveSurveyDemographics$(demographicsDto: IDemographicsDto) {
    return this.api
      .post(Api.surveys.takeaway.campaign.demographics, {
        body: { ...demographicsDto, responseUuid: this.responseUuid },
      })
      .pipe(
        catchError(error => {
          this.zone.run(() => void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]));

          return throwError(error);
        })
      );
  }

  private goToHappyWheelOrAdvertisement(): void {
    if (
      this.campaign.info.wheel?.isActive &&
      this.wheelOfFortuneService.isAgeGroupAllowedForWheelSpin()
    ) {
      void this.router.navigate([WHEEL_OF_FORTUNE_ROUTING_CONSTANTS.ROUTES.HAPPY_WHEEL.MAIN]);
    } else {
      this.goToFinalPage();
    }
  }

  private goToDemographicsForm(): void {
    if (this.campaign.info.wheel?.isDemographicsEnabled && this.campaign.info.wheel?.isActive) {
      void this.router.navigate([
        `${DEMOGRAPHICS_CONSTANTS.ROUTES.MAIN}/${DEMOGRAPHICS_CONSTANTS.ROUTES.FORMS.WHEEL_OF_FORTUNE}`,
      ]);
    } else {
      void this.router.navigate([DEMOGRAPHICS_CONSTANTS.ROUTES.MAIN]);
    }
  }

  private getCustomNextStep(
    question: ICampaignQuestion,
    formValue: IQuestionOptionsRawValue | IQuestionFreeTextRawValue
  ): number {
    if (question.type === QuestionType.Text || question.type === QuestionType.Date) {
      return this.getQuestionIndexByUuid(question.nextQuestion) + 1;
    }
    const nextStepIndex: number = (formValue as IQuestionOptionsRawValue).options.findIndex(
      (option: boolean) => option
    );

    return this.getQuestionIndexByUuid(question.options[nextStepIndex].nextQuestion) + 1;
  }

  private getQuestionIndexByUuid(uuid: string): number {
    return this.campaign.campaign.questions.findIndex(
      (campaignQuestion: ICampaignQuestion) => campaignQuestion.uuid === uuid
    );
  }

  private loadQuestion(index: number): void {
    this.previousQuestionIndexes.push(this.currentIndex);
    this.currentIndex = index ? (index > 0 ? index - 1 : index) : null;
    this.currentIndex$.next(this.currentIndex);
  }

  private goToPortal(): void {
    void this.router.navigate([`${FLEXIBLE_PORTAL_CONSTANTS.ROUTES.MAIN}/${this.token}`]);
  }
}
